import {FC, ReactNode, useEffect, useMemo} from "react";
import {ExecutePO, LabelValuePO, PropertyGroupPO} from "@/model/workflow";
import {forEach, isNumber} from "lodash";
import {CurrentExecuteVO, useAgreeExecute, useQueryCurrentExecute, useRevokeExecute} from "@/service/instance.s";
import Button from "@mui/material/Button";
import * as React from "react";
import Stack from "@mui/material/Stack";
import {styled} from "@mui/material/styles";
import {useAllDict} from "@/store/dict.c";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {WriteReject} from "@/widget/ProcessApproval/WriteReject";
import {AssignProduct} from "@/widget/ProcessApproval/AssignProduct";
import {AuthButtonVO} from "@/service/menu.s";
import {WriteInvoicing} from "@/widget/ProcessApproval/WriteInvoicing";
import {useConfirmPayback} from "@/service/payback.s";
import {useConfirmPayment} from "@/service/payment.s";
import {BaseVO} from "@/model/global";
import {ConfirmLeave} from "@/widget/ProcessApproval/ConfirmLeave";
import {checkQueryKey} from "@/helper/method";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import {useTipBox} from "@/widget/TipBox/TipBox";
import {Box} from "@mui/material";

export interface ApprovalButtonProps {
    instanceId: number;
    onButtonClick?: (btn: AuthButtonVO) => void;
    businessId?: number
    updateForm: React.ReactNode;
}

const ActionButton = styled(Button)<{ colors: string }>(({colors}) => ({
    maxWidth: '80px', padding: "0px 4px", fontSize: 10, height: '2rem', color: colors, borderColor: colors
}))

export const ApprovalButton: FC<ApprovalButtonProps> = ({instanceId, businessId, updateForm}) => {
    const {data: currentExecuteData, refetch: refetchExecutes} = useQueryCurrentExecute({id: instanceId}, checkQueryKey(instanceId))
    const {mutateAsync: agreeExecuteMutate, isPending: agreeExecutePending} = useAgreeExecute()
    const {mutateAsync: confirmPaybackMutate, isPending: confirmPaybackPending} = useConfirmPayback()
    const {mutateAsync: confirmPaymentMutate, isPending: confirmPaymentPending} = useConfirmPayment()
    const {mutateAsync: revokeExecuteMutate, isPending: revokeExecutePending} = useRevokeExecute()
    const {setDialogModal, setDrawerModal} = useGlobalActions()
    const [openTipBox, closeTipBox] = useTipBox();
    const {write} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const currentExecute = currentExecuteData?.data || {} as CurrentExecuteVO
    const {handleType} = useAllDict()
    let rejected = false;

    useEffect(() => {
        rejected = false;
    }, []);

    const renderReject = (index: number) => {
        if (!rejected) {
            rejected = true;
            return (
                <ActionButton disabled={agreeExecutePending} loading={agreeExecutePending} variant="outlined" key={`reject${index}`}
                              size={'small'} onClick={() => {
                    setDialogModal({header: "拒绝", body: <WriteReject refetchExecutes={refetchExecutes} instanceId={instanceId}/>, open: true, maxWidth: 'xs'})
                }} colors={'red'}>拒绝</ActionButton>
            );
        }
    };

    // 同意
    const onAgree = async (executeId: string) => {
        const result = await agreeExecuteMutate({
            instanceId: instanceId,
            executeId: executeId,
        });
        if (result.code == 0) {
            setToast({message: result.msg, open: true, duration: 2000, severity: 'success'});
            await refetchExecutes()
        } else {
            setToast({message: result.msg, open: true, severity: 'error', duration: 2000});
        }
    };

    const renderHandle = (execute: ExecutePO, key: number) => {
        const propertyGroups: PropertyGroupPO[] = execute.propertyGroup
        const buttons: ReactNode[] = []
        forEach(propertyGroups, (propertyGroup: PropertyGroupPO, index: number) => {
            const currentHandleType: LabelValuePO = propertyGroup.handleType
            // const  assigneeIds = propertyGroup.assigneeIds
            if (handleType.findIndex(item => item.value == currentHandleType.value) > -1) {
                buttons.push(<ActionButton disabled={confirmPaymentPending || confirmPaybackPending} loading={confirmPaymentPending || confirmPaybackPending} variant="outlined"
                                           key={`handle${index}-${key}`}
                                           size={'small'} onClick={() => onHandle(execute.nodeId, currentHandleType.value)}
                                           colors={'orange'}>{execute.nodeName}</ActionButton>)
            }
        })
        return buttons
    }

    const onHandle = async (executeId: string, handleValue: number) => {
        let result: BaseVO<null>;
        switch (handleValue) {
            case 1:
                setDialogModal({
                    header: "分配专利",
                    body: <AssignProduct productTypeId={handleValue} businessId={businessId} executeId={executeId} refetchExecutes={refetchExecutes} instanceId={instanceId}/>,
                    open: true,
                    maxWidth: 'sm'
                })
                break
            case 2:
                setDialogModal({
                    header: "分配项目",
                    body: <AssignProduct productTypeId={handleValue} businessId={businessId} executeId={executeId} refetchExecutes={refetchExecutes} instanceId={instanceId}/>,
                    open: true,
                    maxWidth: 'sm'
                })
                break
            case 3:
                setDialogModal({
                    header: "分配商标",
                    body: <AssignProduct productTypeId={handleValue} businessId={businessId} executeId={executeId} refetchExecutes={refetchExecutes} instanceId={instanceId}/>,
                    open: true,
                    maxWidth: 'sm'
                })
                break
            case 4:
                setDialogModal({
                    header: "分配版权",
                    body: <AssignProduct productTypeId={handleValue} businessId={businessId} executeId={executeId} refetchExecutes={refetchExecutes} instanceId={instanceId}/>,
                    open: true,
                    maxWidth: 'sm'
                })
                break
            case 5: // 确认缴费
                result = await confirmPaymentMutate({businessId, executeId, instanceId})
                if (result.code == 0) {
                    await refetchExecutes()
                    setToast({open: true, message: result.msg, severity: 'success', variant: 'standard'});
                } else {
                    setToast({open: true, message: result.msg, severity: 'error', variant: 'standard'});
                }
                break
            case 6: // 确认回款
                result = await confirmPaybackMutate({businessId, executeId, instanceId})
                if (result.code == 0) {
                    await refetchExecutes()
                    setToast({open: true, message: result.msg, severity: 'success', variant: 'standard'});
                } else {
                    setToast({open: true, message: result.msg, severity: 'error', variant: 'standard'});
                }
                break
            case 21:
                setDialogModal({
                    header: "填写发票",
                    body: <WriteInvoicing businessId={businessId} executeId={executeId} refetchExecutes={refetchExecutes} instanceId={instanceId}/>,
                    open: true,
                    maxWidth: 'xs'
                })
                break
            case 30:
                setDialogModal({
                    header: "确认请假",
                    body: <ConfirmLeave businessId={businessId} executeId={executeId} refetchExecutes={refetchExecutes} instanceId={instanceId}/>,
                    open: true,
                    maxWidth: 'xs'
                })
                break
        }
    }

    const onUpdate = (executeId: string) => {
        setDrawerModal({write: 'edit', body: updateForm, open: true, id: businessId, hiddenFooter: false, execute: {instanceId, executeId}})
    }

    const buttons = useMemo(() => {
        let buttons: ReactNode[] = [];
        forEach(currentExecute?.executes, (execute: ExecutePO, index: number) => {
            const nodeType = execute?.nodeType
            switch (nodeType) {
                case 0:
                    const executeId = currentExecute?.executes?.map((item) => item.nodeId).join(",");
                    const button = write !== 'edit' ?
                        <Button disabled={agreeExecutePending} loading={agreeExecutePending} variant='contained' key={index} disableElevation={true} color={'info'}
                                sx={{maxWidth: '80px', padding: "0px 4px", fontSize: 10, height: '2rem'}}
                                size={'small'} onClick={() => onUpdate(executeId)}>修改</Button> : <></>
                    buttons.push(button);
                    break
                case 1:
                    buttons.push(renderReject(index));
                    buttons.push(
                        <ActionButton disabled={agreeExecutePending} loading={agreeExecutePending} variant="outlined" key={index}
                                      size={'small'} onClick={() => onAgree(execute.nodeId)} colors={'green'}>同意</ActionButton>,
                    );
                    break;
                case 6:
                    // buttons.push(renderReject(index));
                    buttons = buttons.concat(renderHandle(execute, index))
                    break
            }
        })
        if (currentExecute.revoked) {
            buttons.push(<ActionButton disabled={revokeExecutePending} loading={revokeExecutePending} variant="outlined" key={-999}
                                       size={'small'} onClick={() => {
                openTipBox({
                    children: (
                        <Box width={260}>
                            <DialogTitle>是否撤回审批!</DialogTitle>
                            <DialogActions>
                                <Button disabled={confirmPaybackPending || confirmPaymentPending || revokeExecutePending}
                                        loading={confirmPaybackPending || confirmPaymentPending || revokeExecutePending} color="primary" onClick={() => closeTipBox()}>
                                    取消
                                </Button>
                                <Button
                                    color="error"
                                    disabled={confirmPaybackPending || confirmPaymentPending || revokeExecutePending}
                                    loading={confirmPaybackPending || confirmPaymentPending || revokeExecutePending}
                                    onClick={async () => {
                                        const result = await revokeExecuteMutate(instanceId)
                                        if (result.code == 0) {
                                            setToast({open: true, message: result.msg, severity: 'success', variant: 'standard'});
                                            await refetchExecutes()
                                            closeTipBox()
                                        } else {
                                            setToast({open: true, message: result.msg, severity: 'error', variant: 'standard'});
                                        }
                                    }
                                    }
                                >
                                    确定
                                </Button>
                            </DialogActions>
                        </Box>
                    ),
                });
            }} colors={'orange'}>撤回</ActionButton>)
        }
        return buttons
    }, [currentExecute, write, agreeExecutePending, confirmPaybackPending, confirmPaymentPending, revokeExecutePending])

    return <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} spacing={1}>{buttons}</Stack>
}